<template>
  <v-dialog v-model="showDialog" max-width="750" persistent scrollable>
    <v-card>
      <ModalHeader
        title="SMS Notification Settings"
        icon="mdi-message-cog"
        @close="showDialog = false"
      />
      <v-card-text class="pa-4" style="max-height: 70vh">
        <v-form ref="form">
          <v-switch
            v-model="form.enabled"
            label="Enable SMS notifications"
            color="success"
            hide-details
            class="mb-2"
          ></v-switch>
          <v-text-field
            outlined
            dense
            v-model="form.sender_id"
            label="Sender ID"
            hint="Leave blank to use the sender id configured on the server (.env)"
            persistent-hint
            class="mb-3"
          ></v-text-field>

          <v-divider class="my-3"></v-divider>
          <p class="subtitle-2 grey--text mb-1">Message Templates</p>
          <p class="caption grey--text">
            Available placeholders per message are shown below each template.
            Messages are trimmed to 160 characters when sent.
          </p>

          <div v-for="event in events" :key="event.key" class="mb-2">
            <v-switch
              v-model="form[event.key + '_enabled']"
              :label="event.label"
              color="success"
              hide-details
              dense
            ></v-switch>
            <v-textarea
              v-model="form[event.key + '_template']"
              :disabled="!form[event.key + '_enabled']"
              rows="2"
              auto-grow
              counter="160"
              outlined
              dense
              class="mt-1"
            ></v-textarea>
            <p class="caption grey--text mt-n4">
              Placeholders:
              <code v-for="p in event.placeholders" :key="p" class="mr-1">{{
                "{" + p + "}"
              }}</code>
            </p>
            <v-alert
              v-if="templateLength(event.key) > 160"
              dense
              text
              type="warning"
              class="caption mb-2"
            >
              This template is {{ templateLength(event.key) }} characters —
              anything past 160 will be cut off when the SMS is sent.
              Placeholder values can make the final message even longer.
            </v-alert>
          </div>

          <v-divider class="my-3"></v-divider>
          <p class="subtitle-2 grey--text mb-2">Test SMS</p>
          <v-row dense align="center">
            <v-col cols="8">
              <v-text-field
                outlined
                v-model="test_phone"
                label="Phone number (09XXXXXXXXX)"
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-btn
                small
                color="secondary"
                :loading="test_loading"
                :disabled="!test_phone"
                @click="sendTest"
                >Send Test SMS</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <p class="caption grey--text mb-0">smsSettingsModal.vue</p>
        <v-spacer></v-spacer>
        <v-btn text @click="showDialog = false">Cancel</v-btn>
        <v-btn color="primary" :loading="loading" @click="submit"
          >Save Settings</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

const EVENTS = [
  {
    key: "customer_registration",
    label: "Customer Registration",
    placeholders: ["customer_name"],
  },
  {
    key: "status_work_on_process",
    label: "Unit Status: Work on Process",
    placeholders: ["customer_name", "unit", "status", "technician"],
  },
  {
    key: "status_ready_to_pickup",
    label: "Unit Status: Ready to Pick-up",
    placeholders: ["customer_name", "unit", "status", "technician"],
  },
  {
    key: "status_cancelled",
    label: "Unit Status: Cancelled",
    placeholders: ["customer_name", "unit", "status", "technician"],
  },
  {
    key: "invoice_created",
    label: "Invoice Created",
    placeholders: ["customer_name", "invoice_no", "amount", "balance"],
  },
  {
    key: "payment_received",
    label: "Payment Received",
    placeholders: ["customer_name", "amount", "invoice_no", "balance"],
  },
  {
    key: "downpayment_received",
    label: "Downpayment Received",
    placeholders: [
      "customer_name",
      "amount",
      "reference_no",
      "remarks",
      "payment_method",
      "date",
    ],
  },
  {
    key: "downpayment_cancelled",
    label: "Downpayment Cancelled",
    placeholders: [
      "customer_name",
      "amount",
      "reference_no",
      "remarks",
      "payment_method",
      "date",
    ],
  },
  {
    key: "pickup_reminder",
    label: "Weekly Pick-up Reminder (up to 3 months)",
    placeholders: ["customer_name", "unit", "status"],
  },
  {
    key: "invoice_due_reminder",
    label: "Invoice Due Reminder (N days before due)",
    placeholders: ["customer_name", "invoice_no", "amount", "due_date"],
  },
];

export default {
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    const form = { enabled: false, sender_id: "" };
    EVENTS.forEach((e) => {
      form[e.key + "_enabled"] = true;
      form[e.key + "_template"] = "";
    });
    return {
      events: EVENTS,
      loading: false,
      test_loading: false,
      test_phone: "",
      form,
    };
  },
  computed: {
    ...mapGetters({
      sms_settings: "sms/sms_settings",
    }),
    showDialog: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
  },
  watch: {
    sms_settings: {
      handler(val) {
        if (val && Object.keys(val).length) {
          const form = {
            enabled: !!val.enabled,
            sender_id: val.sender_id || "",
          };
          EVENTS.forEach((e) => {
            form[e.key + "_enabled"] = !!val[e.key + "_enabled"];
            form[e.key + "_template"] = val[e.key + "_template"] || "";
          });
          this.form = form;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      update_sms_settings: "sms/update_sms_settings",
      send_test_sms: "sms/send_test_sms",
    }),
    templateLength(key) {
      return (this.form[key + "_template"] || "").length;
    },
    async submit() {
      this.loading = true;
      try {
        await this.update_sms_settings(this.form);
        this.$alert({
          title: "Success",
          message: "SMS settings saved",
          color: "success",
          icon: "mdi-check-circle",
        });
        this.showDialog = false;
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response ? e.response.data.message : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.loading = false;
    },
    async sendTest() {
      this.test_loading = true;
      try {
        await this.send_test_sms(this.test_phone);
        this.$alert({
          title: "Success",
          message: "Test SMS sent to " + this.test_phone,
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        const data = e.response && e.response.data;
        this.$alert({
          title: "Error",
          message:
            (data && (data.error_message || data.message)) ||
            "Failed to send test SMS",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.test_loading = false;
    },
  },
};
</script>
